import { useMemo } from 'react';
import FileUploadHeader from './FileUploadHeader';
import { useTranslation } from 'react-i18next';
import { Table } from 'UI';
import { Card, formattedCurrency, Box, ColConfig, createTableProps } from 'vopay';
import { FileUploadProduct, FileUploadService } from '../types';

type TransactionData = {
  [key: string]: string | number | boolean | undefined;
};

type FileUploadHeaderProps = {
  product: FileUploadProduct;
  service: FileUploadService;
  fileName: string;
  numberOfTransactions: number;
  amount: number;
  currency: string;
  clientAccountsEnabled: boolean;
  transactionData: TransactionData[];
};

enum BaseColumnID {
  'Id' = 'Id',
  'ClientAccountID' = 'ClientAccountID',
  'SenderName' = 'SenderName',
  'ReceiverName' = 'ReceiverName',
  'ReceiverEmailAddress' = 'ReceiverEmailAddress',
  'ReceiverPhoneNumber' = 'ReceiverPhoneNumber',
  'PaymentMethod' = 'PaymentMethod',
  'BankingPaymentRail' = 'BankingPaymentRail',
  'Amount' = 'Amount',
  'Currency' = 'Currency',
  'Type' = 'Type',
  'PaymentType' = 'PaymentType',
  'MessageForRecipient' = 'MessageForRecipient',
  'PaymentFrequency' = 'PaymentFrequency',
  'NameOfFrequency' = 'NameOfFrequency',
  'ScheduleStartDate' = 'ScheduleStartDate',
  'ScheduleEndDate' = 'ScheduleEndDate',
  'SemiMonthlyFrequencyType' = 'SemiMonthlyFrequencyType',
  'EndingAfterPayments' = 'EndingAfterPayments',
  'InvoiceNumber' = 'InvoiceNumber',
  'DocumentID' = 'DocumentID',
  'ClientReferenceNumber' = 'ClientReferenceNumber',
  'TransactionLabel' = 'TransactionLabel',
  'Note' = 'Note',
  'SendNotification' = 'SendNotification',
  'NotificationMethod' = 'NotificationMethod',
  'SendReminder' = 'SendReminder',
  'ReminderFrequency' = 'ReminderFrequency',
  'PaymentMethodDisplayOptions' = 'PaymentMethodDisplayOptions',
  'IframeKey' = 'IframeKey',
}

enum eFlexColumnID {
  'FlexiblePaymentOptions' = 'FlexiblePaymentOptions',
  'MinimumPaymentAccepted' = 'MinimumPaymentAccepted',
  'RemainderPaymentDate' = 'RemainderPaymentDate',
  'NumberOfInstallments' = 'NumberOfInstallments',
  'InstallmentFrequency' = 'InstallmentFrequency',
}

const FileUploadReview = ({
  product,
  service,
  fileName,
  numberOfTransactions,
  amount,
  currency,
  transactionData,
  clientAccountsEnabled,
}: FileUploadHeaderProps) => {
  const { t } = useTranslation('translations', { keyPrefix: 'FileUpload.Review' });

  const getServiceName = (product: FileUploadProduct, service: FileUploadService): string => {
    if (product === 'ePay') return 'eLinx™ Pay';
    if (product === 'eCollect') return 'eLinx™ Collect';
    if (product === 'eFlex') return 'eLinx™ Flexible Payments';
    return service;
  };

  const getTableProps = () => {
    if (product === 'ePay' || product === 'eCollect' || product === 'eFlex') {
      const baseColumns: ColConfig<TransactionData, BaseColumnID>[] = [
        {
          id: BaseColumnID.Id,
          header: t('ELinxTable.id'),
          align: 'left' as const,
          template: (row) => row.data.Id,
        },
        {
          id: BaseColumnID.ClientAccountID,
          header: t('ELinxTable.clientAccountID'),
          align: 'left' as const,
          template: (row) => row.data.ClientAccountID,
        },
        {
          id: BaseColumnID.SenderName,
          header: t('ELinxTable.senderName'),
          align: 'left' as const,
          template: (row) => row.data.SenderName,
        },
        {
          id: BaseColumnID.ReceiverName,
          header: t('ELinxTable.receiverName'),
          align: 'left' as const,
          template: (row) => row.data.ReceiverName,
        },
        {
          id: BaseColumnID.ReceiverEmailAddress,
          header: t('ELinxTable.receiverEmailAddress'),
          align: 'left' as const,
          template: (row) => row.data.ReceiverEmailAddress,
        },
        {
          id: BaseColumnID.ReceiverPhoneNumber,
          header: t('ELinxTable.receiverPhoneNumber'),
          align: 'left' as const,
          template: (row) => row.data.ReceiverPhoneNumber,
        },
        {
          id: BaseColumnID.PaymentMethod,
          header: t('ELinxTable.paymentMethod'),
          align: 'left' as const,
          template: (row) => row.data.PaymentMethod,
        },
        {
          id: BaseColumnID.BankingPaymentRail,
          header: t('ELinxTable.bankingPaymentRail'),
          align: 'left' as const,
          template: (row) => row.data.BankingPaymentRail,
        },
        {
          id: BaseColumnID.Amount,
          header: t('ELinxTable.amount'),
          align: 'left' as const,
          template: (row) => row.data.Amount,
        },
        {
          id: BaseColumnID.Currency,
          header: t('ELinxTable.currency'),
          align: 'left' as const,
          template: (row) => row.data.Currency,
        },
        {
          id: BaseColumnID.Type,
          header: t('ELinxTable.type'),
          align: 'left' as const,
          template: (row) => row.data.Type,
        },
        {
          id: BaseColumnID.PaymentType,
          header: t('ELinxTable.paymentType'),
          align: 'left' as const,
          template: (row) => row.data.PaymentType,
        },
        {
          id: BaseColumnID.MessageForRecipient,
          header: t('ELinxTable.messageForRecipient'),
          align: 'left' as const,
          template: (row) => row.data.MessageForRecipient,
        },
        {
          id: BaseColumnID.PaymentFrequency,
          header: t('ELinxTable.paymentFrequency'),
          align: 'left' as const,
          template: (row) => row.data.PaymentFrequency,
        },
        {
          id: BaseColumnID.NameOfFrequency,
          header: t('ELinxTable.nameOfFrequency'),
          align: 'left' as const,
          template: (row) => row.data.NameOfFrequency,
        },
        {
          id: BaseColumnID.ScheduleStartDate,
          header: t('ELinxTable.scheduleStartDate'),
          align: 'left' as const,
          template: (row) => row.data.ScheduleStartDate,
        },
        {
          id: BaseColumnID.ScheduleEndDate,
          header: t('ELinxTable.scheduleEndDate'),
          align: 'left' as const,
          template: (row) => row.data.ScheduleEndDate,
        },
        {
          id: BaseColumnID.SemiMonthlyFrequencyType,
          header: t('ELinxTable.semiMonthlyFrequencyType'),
          align: 'left' as const,
          template: (row) => row.data.SemiMonthlyFrequencyType,
        },
        {
          id: BaseColumnID.EndingAfterPayments,
          header: t('ELinxTable.endingAfterPayments'),
          align: 'left' as const,
          template: (row) => row.data.EndingAfterPayments,
        },
        {
          id: BaseColumnID.InvoiceNumber,
          header: t('ELinxTable.invoiceNumber'),
          align: 'left' as const,
          template: (row) => row.data.InvoiceNumber,
        },
        {
          id: BaseColumnID.DocumentID,
          header: t('ELinxTable.documentID'),
          align: 'left' as const,
          template: (row) => row.data.DocumentID,
        },
        {
          id: BaseColumnID.ClientReferenceNumber,
          header: t('ELinxTable.clientReferenceNumber'),
          align: 'left' as const,
          template: (row) => row.data.ClientReferenceNumber,
        },
        {
          id: BaseColumnID.TransactionLabel,
          header: t('ELinxTable.transactionLabel'),
          align: 'left' as const,
          template: (row) => row.data.TransactionLabel,
        },
        {
          id: BaseColumnID.Note,
          header: t('ELinxTable.note'),
          align: 'left' as const,
          template: (row) => row.data.Note,
        },
        {
          id: BaseColumnID.SendNotification,
          header: t('ELinxTable.sendNotification'),
          align: 'left' as const,
          template: (row) => row.data.SendNotification,
        },
        {
          id: BaseColumnID.NotificationMethod,
          header: t('ELinxTable.notificationMethod'),
          align: 'left' as const,
          template: (row) => row.data.NotificationMethod,
        },
        {
          id: BaseColumnID.SendReminder,
          header: t('ELinxTable.sendReminder'),
          align: 'left' as const,
          template: (row) => row.data.SendReminder,
        },
        {
          id: BaseColumnID.ReminderFrequency,
          header: t('ELinxTable.reminderFrequency'),
          align: 'left' as const,
          template: (row) => row.data.ReminderFrequency,
        },
        {
          id: BaseColumnID.PaymentMethodDisplayOptions,
          header: t('ELinxTable.paymentMethodDisplayOptions'),
          align: 'left' as const,
          template: (row) => row.data.PaymentMethodDisplayOptions,
        },
        {
          id: BaseColumnID.IframeKey,
          header: t('ELinxTable.iframeKey'),
          align: 'left' as const,
          template: (row) => row.data.IframeKey,
        },
      ];

      // eFlex-specific columns
      const eFlexColumns: ColConfig<TransactionData, eFlexColumnID>[] =
        product === 'eFlex'
          ? [
              {
                id: eFlexColumnID.FlexiblePaymentOptions,
                header: t('ELinxTable.flexiblePaymentOptions'),
                align: 'left' as const,
                template: (row) => row.data.FlexiblePaymentOptions,
              },
              {
                id: eFlexColumnID.MinimumPaymentAccepted,
                header: t('ELinxTable.minimumPaymentAccepted'),
                align: 'left' as const,
                template: (row) => row.data.MinimumPaymentAccepted,
              },
              {
                id: eFlexColumnID.RemainderPaymentDate,
                header: t('ELinxTable.remainderPaymentDate'),
                align: 'left' as const,
                template: (row) => row.data.RemainderPaymentDate,
              },
              {
                id: eFlexColumnID.NumberOfInstallments,
                header: t('ELinxTable.numberOfInstallments'),
                align: 'left' as const,
                template: (row) => row.data.NumberOfInstallments,
              },
              {
                id: eFlexColumnID.InstallmentFrequency,
                header: t('ELinxTable.installmentFrequency'),
                align: 'left' as const,
                template: (row) => row.data.InstallmentFrequency,
              },
            ]
          : [];

      const conditionalColumns: ColConfig<TransactionData, 'Name' | 'Account'>[] = [];

      if (!clientAccountsEnabled && product !== 'ePay' && product !== 'eCollect' && product !== 'eFlex') {
        conditionalColumns.push(
          {
            id: 'Name',
            header: t('ELinxTable.name'),
            align: 'left' as const,
            template: (row) => row.data.Name,
          },
          {
            id: 'Account',
            header: t('ELinxTable.account'),
            align: 'left' as const,
            template: (row) => row.data.Account,
          },
        );
      }

      return {
        id: 'bulkTransactionTable',
        columns: [
          baseColumns[0], // Id
          baseColumns[1], // ClientAccountID
          ...conditionalColumns, // Name and Account if enabled
          ...baseColumns.slice(2), // Rest of the base columns
          ...eFlexColumns, // eFlex-specific columns if product is eFlex
        ],
        rows:
          Object.values(transactionData).map((transaction, index) => ({
            id: `transaction-${index}`,
            data: {
              Id: String(index + 1),
              ClientAccountID: transaction.ClientAccountID ? String(transaction.ClientAccountID) : '',
              SenderName: transaction.SenderName ? String(transaction.SenderName) : '',
              ReceiverName: transaction.ReceiverName ? String(transaction.ReceiverName) : '',
              ReceiverEmailAddress: transaction.ReceiverEmailAddress ? String(transaction.ReceiverEmailAddress) : '',
              ReceiverPhoneNumber: transaction.ReceiverPhoneNumber ? String(transaction.ReceiverPhoneNumber) : '',
              PaymentMethod: transaction.PaymentMethod ? String(transaction.PaymentMethod) : '',
              BankingPaymentRail: transaction.BankingPaymentRail ? String(transaction.BankingPaymentRail) : '',
              Name: String(
                transaction.CompanyName
                  ? transaction.CompanyName
                  : transaction.RecipientName
                    ? transaction.RecipientName
                    : transaction.ReceiverName
                      ? transaction.ReceiverName
                      : `${String(transaction.FirstName)} ${String(transaction.LastName)}`,
              ),
              Account: (
                <>
                  {transaction.CardNumber ? transaction.CardNumber : ''}
                  {transaction.CreditCardNumber ? transaction.CreditCardNumber : ''}
                  {transaction.ReceiverEmailAddress ? transaction.ReceiverEmailAddress : ''}
                  {transaction.FinancialInstitutionNumber ? `${String(transaction.FinancialInstitutionNumber)}-` : ''}
                  {transaction.BranchTransitNumber ? `${String(transaction.BranchTransitNumber)}-` : ''}
                  {transaction.AccountNumber ? `${String(transaction.AccountNumber)}` : ''}
                  {transaction.Token ? t('Table.Row.accountToken', { token: transaction.Token }) : ''}
                  {transaction.CreditCardToken
                    ? t('Table.Row.accountCreditCardToken', { token: transaction.CreditCardToken })
                    : ''}
                  {transaction.CardToken ? t('Table.Row.accountCardToken', { token: transaction.CardToken }) : ''}
                  {transaction.FlinksAccountID
                    ? t('Table.Row.accountFlinksAccountID', { token: transaction.FlinksAccountID })
                    : ''}
                  {transaction.FlinksLoginID
                    ? t('Table.Row.accountFlinksLoginID', { token: transaction.FlinksLoginID })
                    : ''}
                  {transaction.PlaidPublicToken
                    ? t('Table.Row.accountPlaidPublicToken', { token: transaction.PlaidPublicToken })
                    : ''}
                  {transaction.PlaidAccessToken
                    ? t('Table.Row.accountPlaidAccessToken', { token: transaction.PlaidAccessToken })
                    : ''}
                  {transaction.PlaidAccountID
                    ? t('Table.Row.accountPlaidAccountID', { token: transaction.PlaidAccountID })
                    : ''}
                  {transaction.PlaidProcessorToken
                    ? t('Table.Row.accountPlaidProcessorToken', { token: transaction.PlaidProcessorToken })
                    : ''}
                  {transaction.InveriteRequestGUID
                    ? t('Table.Row.accountInveriteRequestGUID', { token: transaction.InveriteRequestGUID })
                    : ''}
                </>
              ),
              Amount: `$ ${formattedCurrency(String(transaction.Amount || ''))}`,
              Currency: transaction.Currency ? String(transaction.Currency) : '',
              Type: transaction.Frequency
                ? t('Table.Row.typeScheduled')
                : transaction.Amount
                  ? t('Table.Row.typeTransaction')
                  : t('Table.Row.typeBeneficiary'),
              PaymentType: transaction.PaymentType ? String(transaction.PaymentType) : '',
              MessageForRecipient: transaction.RecipientMessage ? String(transaction.RecipientMessage) : '',
              PaymentFrequency: transaction.Frequency ? String(transaction.Frequency) : '',
              NameOfFrequency: transaction.NameOfFrequency ? String(transaction.NameOfFrequency) : '',
              ScheduleStartDate: transaction.ScheduleStartDate ? String(transaction.ScheduleStartDate) : '',
              ScheduleEndDate: transaction.ScheduleEndDate ? String(transaction.ScheduleEndDate) : '',
              SemiMonthlyFrequencyType: transaction.SemiMonthlyFrequencyType
                ? String(transaction.SemiMonthlyFrequencyType)
                : '',
              EndingAfterPayments: transaction.EndingAfterPayments ? String(transaction.EndingAfterPayments) : '',
              InvoiceNumber: transaction.InvoiceNumber ? String(transaction.InvoiceNumber) : '',
              DocumentID: transaction.DocumentID ? String(transaction.DocumentID) : '',
              ClientReferenceNumber: transaction.ClientReferenceNumber ? String(transaction.ClientReferenceNumber) : '',
              TransactionLabel: transaction.TransactionLabel ? String(transaction.TransactionLabel) : '',
              Note: transaction.Note ? String(transaction.Note) : '',
              SendNotification: transaction.SendNotification ? String(transaction.SendNotification) : '',
              NotificationMethod: transaction.NotificationMethod ? String(transaction.NotificationMethod) : '',
              SendReminder: transaction.SendReminder ? String(transaction.SendReminder) : '',
              ReminderFrequency: transaction.ReminderFrequency ? String(transaction.ReminderFrequency) : '',
              PaymentMethodDisplayOptions: transaction.PaymentMethodDisplayOptions
                ? String(transaction.PaymentMethodDisplayOptions)
                : '',
              IframeKey: transaction.IframeKey ? String(transaction.IframeKey) : '',
              // eFlex-specific fields
              FlexiblePaymentOptions: transaction.FlexiblePaymentOptions
                ? String(transaction.FlexiblePaymentOptions)
                : '',
              MinimumPaymentAccepted: transaction.MinimumPaymentAccepted
                ? `$ ${formattedCurrency(String(transaction.MinimumPaymentAccepted))}`
                : '',
              RemainderPaymentDate: transaction.RemainderPaymentDate ? String(transaction.RemainderPaymentDate) : '',
              NumberOfInstallments: transaction.NumberOfInstallments ? String(transaction.NumberOfInstallments) : '',
              InstallmentFrequency: transaction.InstallmentFrequency ? String(transaction.InstallmentFrequency) : '',
            },
          })) || [],
      };
    } else if (product === 'Client Account Transfer') {
      return createTableProps({
        id: 'bulkTransactionTable',
        columns: [
          {
            id: 'Id',
            header: t('ClientAccountTransferTable.id'),
            align: 'left' as const,
            template: (row) => row.data.Id,
          },
          {
            id: 'TransferType',
            header: t('ClientAccountTransferTable.transferType'),
            align: 'left' as const,
            template: (row) => row.data.TransferType,
          },
          {
            id: 'DebitorClientAccountID',
            header: t('ClientAccountTransferTable.debitorClientAccountID'),
            align: 'left' as const,
            template: (row) => row.data.DebitorClientAccountID,
          },
          {
            id: 'RecipientClientAccountID',
            header: t('ClientAccountTransferTable.recipientClientAccountID'),
            align: 'left' as const,
            template: (row) => row.data.RecipientClientAccountID,
          },
          {
            id: 'Amount',
            header: t('ClientAccountTransferTable.amount'),
            align: 'left' as const,
            template: (row) => row.data.Amount,
          },
          {
            id: 'FundingRail',
            header: t('ClientAccountTransferTable.fundingRail'),
            align: 'left' as const,
            template: (row) => row.data.FundingRail,
          },
          {
            id: 'WithdrawRail',
            header: t('ClientAccountTransferTable.withdrawRail'),
            align: 'left' as const,
            template: (row) => row.data.WithdrawRail,
          },
          {
            id: 'Currency',
            header: t('ClientAccountTransferTable.currency'),
            align: 'left' as const,
            template: (row) => row.data.Currency,
          },
          {
            id: 'ReverseOnFailure',
            header: t('ClientAccountTransferTable.reverseOnFailure'),
            align: 'left' as const,
            template: (row) => row.data.ReverseOnFailure,
          },
          {
            id: 'ClientReferenceNumber',
            header: t('ClientAccountTransferTable.clientReferenceNumber'),
            align: 'left' as const,
            template: (row) => row.data.ClientReferenceNumber,
          },
          {
            id: 'Notes',
            header: t('ClientAccountTransferTable.notes'),
            align: 'left' as const,
            template: (row) => row.data.Notes,
          },
          {
            id: 'TransactionLabel',
            header: t('ClientAccountTransferTable.transactionLabel'),
            align: 'left' as const,
            template: (row) => row.data.TransactionLabel,
          },
        ],
        rows:
          Object.values(transactionData).map((transaction, index) => ({
            id: `transfer-${index}`,
            data: {
              Id: String(index + 1),
              TransferType: transaction.TransferType ? String(transaction.TransferType) : '',
              DebitorClientAccountID: transaction.DebitorClientAccountID
                ? String(transaction.DebitorClientAccountID)
                : '',
              RecipientClientAccountID: transaction.RecipientClientAccountID
                ? String(transaction.RecipientClientAccountID)
                : '',
              Amount: transaction.Amount ? `$ ${formattedCurrency(String(transaction.Amount))}` : '',
              FundingRail: transaction.FundingRail ? String(transaction.FundingRail) : '',
              WithdrawRail: transaction.WithdrawRail ? String(transaction.WithdrawRail) : '',
              Currency: transaction.Currency ? String(transaction.Currency) : '',
              ReverseOnFailure: transaction.ReverseOnFailure !== undefined ? String(transaction.ReverseOnFailure) : '',
              ClientReferenceNumber: transaction.ClientReferenceNumber ? String(transaction.ClientReferenceNumber) : '',
              Notes: transaction.Notes ? String(transaction.Notes) : '',
              TransactionLabel: transaction.TransactionLabel ? String(transaction.TransactionLabel) : '',
            },
          })) || [],
      });
    }

    const defaultColumns: ColConfig<TransactionData, 'Id' | 'Amount' | 'Service'>[] = [
      {
        id: 'Id',
        header: t('Table.id'),
        align: 'left' as const,
        template: (row) => row.data.Id,
      },
      {
        id: 'Amount',
        header: t('Table.amount'),
        align: 'left' as const,
        template: (row) => row.data.Amount,
      },
      {
        id: 'Service',
        header: t('Table.service'),
        align: 'left' as const,
        template: (row) => row.data.Service,
      },
    ];

    const conditionalDefaultColumns: ColConfig<TransactionData, 'ClientAccountID' | 'Name' | 'Account'>[] = [];

    if (clientAccountsEnabled) {
      conditionalDefaultColumns.push({
        id: 'ClientAccountID',
        header: t('Table.clientAccountID'),
        align: 'left' as const,
        template: (row) => row.data.ClientAccountID,
      });
    } else {
      conditionalDefaultColumns.push(
        {
          id: 'Name',
          header: t('Table.name'),
          align: 'left' as const,
          template: (row) => row.data.Name,
        },
        {
          id: 'Account',
          header: t('Table.account'),
          align: 'left' as const,
          template: (row) => row.data.Account,
        },
      );
    }

    return {
      id: 'bulkTransactionTable',
      columns: [
        defaultColumns[0], // Id
        ...conditionalDefaultColumns, // ClientAccountID, Name, Account based on conditions
        ...defaultColumns.slice(1), // Amount, Service
      ],
      rows:
        Object.values(transactionData).map((transaction, index) => ({
          id: `default-${index}`,
          data: {
            Id: String(index + 1),
            ClientAccountID: transaction.ClientAccountID ? String(transaction.ClientAccountID) : '',
            Name: String(
              transaction.CompanyName
                ? transaction.CompanyName
                : transaction.RecipientName
                  ? transaction.RecipientName
                  : transaction.ReceiverName
                    ? transaction.ReceiverName
                    : `${String(transaction.FirstName)} ${String(transaction.LastName)}`,
            ),
            Account: (
              <>
                {transaction.CardNumber ? transaction.CardNumber : ''}
                {transaction.CreditCardNumber ? transaction.CreditCardNumber : ''}
                {transaction.ReceiverEmailAddress ? transaction.ReceiverEmailAddress : ''}
                {transaction.Email ? transaction.Email : ''}
                {transaction.FinancialInstitutionNumber ? `${String(transaction.FinancialInstitutionNumber)}-` : ''}
                {transaction.BranchTransitNumber ? `${String(transaction.BranchTransitNumber)}-` : ''}
                {transaction.AccountNumber ? `${String(transaction.AccountNumber)}` : ''}
                {transaction.Token ? t('Table.Row.accountToken', { token: transaction.Token }) : ''}
                {transaction.CreditCardToken
                  ? t('Table.Row.accountCreditCardToken', { token: transaction.CreditCardToken })
                  : ''}
                {transaction.CardToken ? t('Table.Row.accountCardToken', { token: transaction.CardToken }) : ''}
                {transaction.FlinksAccountID
                  ? t('Table.Row.accountFlinksAccountID', { token: transaction.FlinksAccountID })
                  : ''}
                {transaction.FlinksLoginID
                  ? t('Table.Row.accountFlinksLoginID', { token: transaction.FlinksLoginID })
                  : ''}
                {transaction.PlaidPublicToken
                  ? t('Table.Row.accountPlaidPublicToken', { token: transaction.PlaidPublicToken })
                  : ''}
                {transaction.PlaidAccessToken
                  ? t('Table.Row.accountPlaidAccessToken', { token: transaction.PlaidAccessToken })
                  : ''}
                {transaction.PlaidAccountID
                  ? t('Table.Row.accountPlaidAccountID', { token: transaction.PlaidAccountID })
                  : ''}
                {transaction.PlaidProcessorToken
                  ? t('Table.Row.accountPlaidProcessorToken', { token: transaction.PlaidProcessorToken })
                  : ''}
                {transaction.InveriteRequestGUID
                  ? t('Table.Row.accountInveriteRequestGUID', { token: transaction.InveriteRequestGUID })
                  : ''}
              </>
            ),
            Amount: `$${formattedCurrency((transaction.Amount || '').toString() || '')}`,
            Service: getServiceName(product, service),
          },
        })) || [],
    };
  };

  const TableProps = useMemo(() => {
    return getTableProps() as any;
  }, [transactionData]);

  return (
    <div>
      <Box flx="col" pb="lg" mb="md" gap="sm" jc="sb" width="100%" wrap="wrap">
        <FileUploadHeader
          product={product}
          service={service}
          fileName={fileName}
          numberOfTransactions={numberOfTransactions}
          amount={amount}
          currency={currency}
        />
        <Card style={{ padding: '16px' }}>
          <div style={{ width: '100%', overflowX: 'auto' }}>
            <Table version="2" zebra {...TableProps} />
          </div>
        </Card>
      </Box>
    </div>
  );
};

export default FileUploadReview;
